import { useState } from 'react';
import type { LayerPermissionEffect, LayerPermissionKind } from '../../lib/layersApi';
import type { Group, RolesOverviewMember } from '../../lib/types';

export type AccessRow = {
  id: string;
  principal_type: 'user' | 'group';
  principal_id: string;
  principal_name: string;
  permission: LayerPermissionKind;
  effect: LayerPermissionEffect;
};

function principalLabel(m: RolesOverviewMember): string {
  const full = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return full || m.email || '—';
}

/** One "chips + picker" block for a single (permission, effect) pair — e.g.
 * view+deny ("Всем, кроме"), edit+grant ("Дополнительно"), edit+deny. */
function PrincipalList({
  rows,
  members,
  groups,
  onAdd,
  onRemove,
  busy,
  placeholder,
  emptyLabel,
}: {
  rows: AccessRow[];
  members: RolesOverviewMember[];
  groups: Group[];
  onAdd: (principalType: 'user' | 'group', principalId: string, principalName: string) => void;
  onRemove: (rowId: string) => void;
  busy?: boolean;
  placeholder: string;
  emptyLabel: string;
}) {
  const [picked, setPicked] = useState('');

  const handleAdd = () => {
    if (!picked) return;
    const [principalType, principalId] = picked.split(':') as ['user' | 'group', string];
    const name =
      principalType === 'user'
        ? principalLabel(members.find((m) => m.user_id === principalId)!)
        : (groups.find((g) => g.id === principalId)?.name ?? '—');
    onAdd(principalType, principalId, name);
    setPicked('');
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {rows.length === 0 && <span className="text-[11px] text-text-secondary">{emptyLabel}</span>}
        {rows.map((r) => (
          <span
            key={r.id}
            className="inline-flex items-center gap-1.5 rounded-full bg-bg-surface-secondary px-2.5 py-1 text-[10px] font-medium text-text-primary"
          >
            {r.principal_type === 'group' ? '◫ ' : ''}
            {r.principal_name}
            <button type="button" onClick={() => onRemove(r.id)} className="text-text-secondary hover:text-status-danger">
              ×
            </button>
          </span>
        ))}
      </div>

      <div className="mt-2 flex items-center gap-2">
        <select
          value={picked}
          onChange={(e) => setPicked(e.target.value)}
          className="h-9 flex-1 rounded-[9px] border border-border-default bg-bg-surface px-2 text-[12px] text-text-primary outline-none"
        >
          <option value="">{placeholder}</option>
          {members
            .filter((m) => !rows.some((r) => r.principal_type === 'user' && r.principal_id === m.user_id))
            .map((m) => (
              <option key={m.user_id} value={`user:${m.user_id}`}>
                {principalLabel(m)}
              </option>
            ))}
          {groups
            .filter((g) => !rows.some((r) => r.principal_type === 'group' && r.principal_id === g.id))
            .map((g) => (
              <option key={g.id} value={`group:${g.id}`}>
                Группа: {g.name}
              </option>
            ))}
        </select>
        <button
          type="button"
          onClick={handleAdd}
          disabled={busy || !picked}
          className="flex h-9 shrink-0 items-center rounded-[9px] bg-action-primary px-3 text-[12px] font-medium text-text-inverse disabled:cursor-not-allowed disabled:opacity-50"
        >
          Добавить
        </button>
      </div>
    </div>
  );
}

/** Two dimensions (просмотр / редактирование объектов и атрибутов), each with a
 * role-based default the caller describes via defaultViewLabel/defaultEditLabel:
 * view defaults to every workspace member, edit defaults to workspace_admin +
 * редактор карты (gis_admin) + оператор слоёв. Per-layer overrides on top of
 * that default are what this editor manages — an exclusion list for view
 * ("Всем, кроме"), and both an exclusion and an addition list for edit, since
 * the edit default is role-based rather than universal. workspace_admin is
 * never offered in `members`/`groups` — the caller filters it out, since a
 * workspace_admin always has full access regardless of any row here. */
export function LayerAccessEditor({
  rows,
  members,
  groups,
  onAdd,
  onRemove,
  busy,
  error,
}: {
  rows: AccessRow[];
  members: RolesOverviewMember[];
  groups: Group[];
  onAdd: (
    principalType: 'user' | 'group',
    principalId: string,
    principalName: string,
    permission: LayerPermissionKind,
    effect: LayerPermissionEffect,
  ) => void;
  onRemove: (rowId: string) => void;
  busy?: boolean;
  error?: string | null;
}) {
  const viewDeny = rows.filter((r) => r.permission === 'view' && r.effect === 'deny');
  const editGrant = rows.filter((r) => r.permission === 'edit' && r.effect === 'grant');
  const editDeny = rows.filter((r) => r.permission === 'edit' && r.effect === 'deny');

  return (
    <div className="flex flex-col gap-5">
      {error && <p className="text-xs font-medium text-status-warning">{error}</p>}

      <div>
        <h3 className="text-[13px] font-semibold text-text-primary">Просмотр</h3>
        <p className="mt-1 text-xs text-text-secondary">
          По умолчанию слой виден всем участникам пространства. Здесь можно исключить конкретных людей или группы.
        </p>
        <div className="mt-3">
          <p className="text-[11px] font-medium text-text-secondary">Всем, кроме:</p>
          <div className="mt-1.5">
            <PrincipalList
              rows={viewDeny}
              members={members}
              groups={groups}
              busy={busy}
              placeholder="Исключить из просмотра…"
              emptyLabel="Исключений нет — видят все"
              onAdd={(t, id, name) => onAdd(t, id, name, 'view', 'deny')}
              onRemove={onRemove}
            />
          </div>
        </div>
      </div>

      <div className="h-px bg-border-default" />

      <div>
        <h3 className="text-[13px] font-semibold text-text-primary">Редактирование объектов и атрибутов</h3>
        <p className="mt-1 text-xs text-text-secondary">
          По умолчанию редактируют администратор пространства, редактор карты и оператор слоёв. Можно расширить или
          сузить этот список для конкретного слоя.
        </p>
        <div className="mt-3">
          <p className="text-[11px] font-medium text-text-secondary">Дополнительно предоставлено:</p>
          <div className="mt-1.5">
            <PrincipalList
              rows={editGrant}
              members={members}
              groups={groups}
              busy={busy}
              placeholder="Выдать доступ к редактированию…"
              emptyLabel="Дополнительного доступа не выдано"
              onAdd={(t, id, name) => onAdd(t, id, name, 'edit', 'grant')}
              onRemove={onRemove}
            />
          </div>
        </div>
        <div className="mt-3">
          <p className="text-[11px] font-medium text-text-secondary">Всем, кроме:</p>
          <div className="mt-1.5">
            <PrincipalList
              rows={editDeny}
              members={members}
              groups={groups}
              busy={busy}
              placeholder="Исключить из редактирования…"
              emptyLabel="Исключений нет"
              onAdd={(t, id, name) => onAdd(t, id, name, 'edit', 'deny')}
              onRemove={onRemove}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
